import React from 'react';

import { Spin } from 'antd-v5';
import {
  MdCheckCircle,
  MdCloudUpload,
  MdError,
  MdFactCheck,
  MdInsertDriveFile,
  MdPendingActions,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { ImportFileRequirementRow } from '@appTypes/esb';

import type { CampPhase, CampView } from '../../types';
import { parseCampSummary, toCampCount } from '../../utils';
import CampImportErrors from '../CampImportErrors';
import styles from './CampImportStatus.module.scss';

const MetadataRow: React.FC<{
  labelId: string;
  value: React.ReactNode;
}> = props => (
  <div className={styles.metaRow}>
    <Text font="body-overline">
      <span className={styles.metaLabel}>
        <FormattedMessage id={props.labelId} />
      </span>
    </Text>
    <Text font="body-overline">
      <span className={styles.muted}>{props.value}</span>
    </Text>
  </div>
);

const ImportMetadata: React.FC<{
  view: CampView;
  hideTotalRows?: boolean;
}> = props => {
  const { view, hideTotalRows } = props;

  return (
    <div className={styles.metadata}>
      {view.totalRows !== null && !hideTotalRows && (
        <MetadataRow
          labelId="advancementImport.camp.meta.totalRows"
          value={view.totalRows}
        />
      )}
      {!!view.dateUploaded && (
        <MetadataRow
          labelId="advancementImport.camp.meta.uploaded"
          value={view.dateUploaded}
        />
      )}
      {!!view.dateValidated && (
        <MetadataRow
          labelId="advancementImport.camp.meta.validated"
          value={view.dateValidated}
        />
      )}
      {!!view.dateProcessed && (
        <MetadataRow
          labelId="advancementImport.camp.meta.processed"
          value={view.dateProcessed}
        />
      )}
      {!!view.processingMsg && (
        <MetadataRow
          labelId="advancementImport.camp.meta.result"
          value={view.processingMsg}
        />
      )}
    </div>
  );
};

interface CampImportStatusProps {
  /** Mapped status; null during the initial upload POST before status arrives. */
  view: CampView | null;
  /** Name of the file being uploaded (before server status carries it). */
  fileName: string;
  /** The initial upload POST failed (no server status will arrive). */
  uploadError: boolean;
  cancelling: boolean;
  /** The import was already running when the modal opened; badged only when its file name is known. */
  isPrevious?: boolean;
  /** Raw summaryReport, shown so the user can judge before starting the import */
  summaryReport?: string;
  /** Rows that failed validation; empty until the confirm step reports errors */
  errorRows?: ImportFileRequirementRow[];
  errorCount?: number;
  errorsLoading?: boolean;
  starting?: boolean;
  onStart?: () => void;
  onCancel: () => void;
}

/** Presentational status panel for the async CSV (merit-badge) import flow. */
const CampImportStatus: React.FC<CampImportStatusProps> = props => {
  const {
    view,
    fileName,
    uploadError,
    cancelling,
    isPrevious,
    summaryReport,
    errorRows,
    errorCount,
    errorsLoading,
    starting,
    onStart,
    onCancel,
  } = props;

  const phase: CampPhase = view
    ? view.phase
    : uploadError
    ? 'error'
    : 'uploading';
  const isDone = view?.isDone ?? false;
  const isError = (view?.isError ?? false) || uploadError;
  const displayName = view?.fileName || fileName;
  // A finished import still needs an action, otherwise it occupies the zone
  // forever and no further .csv can be uploaded.
  const showAction = !!view || uploadError;
  const needsAction = !!view?.needsAction && !!onStart;
  const summary = parseCampSummary(summaryReport);

  // The spinner means "the server is working"; while validation waits on the
  // user nothing is in flight, so the icon reflects the step instead
  const renderStatusIcon = () => {
    if (isDone)
      return (
        <MdCheckCircle
          className={styles.statusIcon}
          color={cssVars['color-success']}
        />
      );
    if (isError)
      return (
        <MdError className={styles.statusIcon} color={cssVars['color-error']} />
      );
    if (starting) return <Spin size="small" />;

    switch (phase) {
      case 'uploading':
        // No view yet means the POST is still in flight; once the server holds
        // the file it is queued work, not an upload
        return view ? (
          <Spin size="small" />
        ) : (
          <MdCloudUpload
            className={styles.statusIcon}
            color={cssVars['color-text-light']}
          />
        );
      case 'validating':
        return (
          <MdFactCheck
            className={styles.statusIcon}
            color={cssVars['color-text-light']}
          />
        );
      case 'awaitingProcess':
        return (
          <MdPendingActions
            className={styles.statusIcon}
            color={cssVars['color-info']}
          />
        );
      default:
        return <Spin size="small" />;
    }
  };

  return (
    <div className={styles.container}>
      <Text font="body-body-2">
        <span className={styles.heading}>
          <FormattedMessage id="advancementImport.files" />:
        </span>
      </Text>

      <div className={styles.item}>
        <MdInsertDriveFile className={styles.fileIcon} />
        <div className={styles.body}>
          <div className={styles.titleRow}>
            {!!displayName && <Text font="body-caption">{displayName}</Text>}
            <div className={styles.statusRow}>
              <Text font="body-caption">
                <span className={isError ? styles.errorText : styles.muted}>
                  <FormattedMessage
                    id="advancementImport.camp.status"
                    values={{
                      status: view ? (
                        view.processStatus
                      ) : uploadError ? (
                        <FormattedMessage id="advancementImport.camp.error" />
                      ) : (
                        <FormattedMessage id="advancementImport.camp.uploading" />
                      ),
                    }}
                  />
                </span>
              </Text>
              {renderStatusIcon()}
            </div>
          </div>

          {isPrevious && !!displayName && (
            <Text font="body-overline">
              <span className={styles.previousBadge}>
                <FormattedMessage id="advancementImport.camp.previousUpload" />
              </span>
            </Text>
          )}

          {summary && (
            <div className={styles.summary}>
              <MetadataRow
                labelId="advancementImport.camp.summary.rows"
                value={toCampCount(summary.RowCount)}
              />
              <MetadataRow
                labelId={
                  needsAction
                    ? 'advancementImport.camp.summary.willImport'
                    : 'advancementImport.camp.summary.imported'
                }
                value={toCampCount(summary.RowForProcess)}
              />
              <MetadataRow
                labelId="advancementImport.camp.summary.withErrors"
                value={toCampCount(summary.RowWithErrors)}
              />
            </div>
          )}

          {view && (
            <ImportMetadata
              view={view}
              hideTotalRows={summary?.RowCount !== undefined}
            />
          )}

          {needsAction && !!errorCount && (
            <CampImportErrors
              rows={errorRows ?? []}
              count={errorCount}
              loading={!!errorsLoading}
              fileName={displayName}
            />
          )}

          {(needsAction || showAction) && (
            <div className={styles.actions}>
              {showAction && (
                <Button
                  type="default"
                  size="small"
                  danger={!isDone}
                  loading={cancelling}
                  onClick={onCancel}
                >
                  <FormattedMessage
                    id={
                      isDone
                        ? 'advancementImport.camp.importAnother'
                        : 'advancementImport.camp.cancel'
                    }
                  />
                </Button>
              )}
              {needsAction && (
                <Button
                  type="primary"
                  size="small"
                  loading={starting}
                  onClick={onStart}
                >
                  <FormattedMessage id="advancementImport.camp.startImport" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampImportStatus;
